export interface DocContent {
  title: string;
  content: string;
  /** Optional logo shown next to the title (e.g. a connector's CDN icon). */
  icon?: string;
}

export interface DocNavItem {
  title: string;
  slug: string;
  children?: DocNavItem[];
}

export interface DocSection {
  title: string;
  items: DocNavItem[];
}

export const docsSections: DocSection[] = [
  {
    title: 'Getting Started',
    items: [
      { title: 'Intro to Pliable Concepts', slug: 'intro-to-pliable-concepts' },
      {
        title: 'Connecting to Snowflake',
        slug: 'connecting-to-snowflake',
        children: [
          { title: 'Migrating to Keypair Auth', slug: 'migrating-to-keypair-auth' },
        ],
      },
      { title: 'Uploading a New File', slug: 'uploading-a-new-file' },
    ],
  },
  {
    title: 'Core Concepts',
    items: [
      { title: 'Data Sources', slug: 'data-sources' },
      {
        title: 'Data Cleaning',
        slug: 'data-cleaning',
        children: [
          { title: 'Column Formatting', slug: 'column-formatting' },
          { title: 'Filtering', slug: 'filtering' },
          { title: 'Grouping Behaviors', slug: 'grouping-behaviors' },
        ],
      },
      { title: 'Data Searching', slug: 'data-searching' },
      {
        title: 'Data Modeling',
        slug: 'data-modeling',
        children: [
          { title: 'Entities', slug: 'entities' },
          { title: 'Metrics', slug: 'metrics' },
          { title: 'Relationships', slug: 'relationships' },
        ],
      },
      { title: 'Data Mapping', slug: 'data-mapping' },
      {
        title: 'Data Visualization',
        slug: 'data-visualization',
        children: [
          { title: 'Charts', slug: 'charts' },
          { title: 'Dashboards', slug: 'dashboards' },
        ],
      },
      { title: 'Running Builds', slug: 'running-builds' },
      { title: 'Ellie', slug: 'ask-ai' },
      { title: 'LLM Metering', slug: 'llm-metering' },
      { title: 'Roles', slug: 'roles' },
      { title: 'White Labeling', slug: 'whitelabeling' },
      { title: 'Production vs Development', slug: 'production-vs-development-mode' },
      { title: 'MCP Server', slug: 'mcp-server' },
      {
        title: 'Pliable API',
        slug: 'pliable-api',
        children: [
          { title: 'Uploading Files via API', slug: 'uploading-files-via-api' },
          { title: 'Embedding Pliable', slug: 'embedding-pliable' },
          { title: 'Programmatic User Management', slug: 'programmatic-user-management' },
          { title: 'Ellie Invoke API', slug: 'ellie-invoke-api' },
          { title: 'Ellie Streaming API', slug: 'ellie-streaming-api' },
          { title: 'Knowledge Base API', slug: 'knowledge-base-api' },
        ],
      },
    ],
  },
  {
    // One page per connector; the slug is the connector id (e.g. plb_netsuite_suiteql)
    // so a connector maps 1:1 to /docs/<connector id>.
    title: 'Connectors',
    items: [
      { title: 'NetSuite (SuiteQL)', slug: 'plb_netsuite_suiteql' },
    ],
  },
];

// Flatten the nav tree to get ordered list of all slugs (for prev/next navigation)
function flattenSlugs(sections: DocSection[]): string[] {
  const slugs: string[] = [];
  for (const section of sections) {
    for (const item of section.items) {
      slugs.push(item.slug);
      if (item.children) {
        for (const child of item.children) {
          slugs.push(child.slug);
        }
      }
    }
  }
  return slugs;
}

export const allSlugs = flattenSlugs(docsSections);

// Find breadcrumb path for a given slug
export function getBreadcrumb(slug: string): { section: string; parent?: DocNavItem; current: DocNavItem } | null {
  for (const section of docsSections) {
    for (const item of section.items) {
      if (item.slug === slug) {
        return { section: section.title, current: item };
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.slug === slug) {
            return { section: section.title, parent: item, current: child };
          }
        }
      }
    }
  }
  return null;
}

// Get prev/next slugs for navigation
export function getPrevNext(slug: string): { prev?: DocNavItem; next?: DocNavItem } {
  const flatItems = flattenItems(docsSections);
  const idx = flatItems.findIndex(i => i.slug === slug);
  return {
    prev: idx > 0 ? flatItems[idx - 1] : undefined,
    next: idx < flatItems.length - 1 ? flatItems[idx + 1] : undefined,
  };
}

function flattenItems(sections: DocSection[]): DocNavItem[] {
  const items: DocNavItem[] = [];
  for (const section of sections) {
    for (const item of section.items) {
      items.push(item);
      if (item.children) {
        for (const child of item.children) {
          items.push(child);
        }
      }
    }
  }
  return items;
}
